import { requestSuggestions } from './suggestions';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Automatic requests are reserved by Rails after committing the turn. The
// browser only reads them; reload/reconnect never creates another inference.
export async function readAutomaticResult(chatId, messageId, feature, { fetcher = fetch, signal,
  sleep = ms => new Promise(resolve => setTimeout(resolve, ms)) } = {}) {
  if (![chatId, messageId].every(id => UUID.test(id || '')) || !['suggestions', 'correction'].includes(feature)) throw new Error('invalid');
  for (let attempt = 0; attempt < 46; attempt++) {
    if (signal?.aborted) throw new Error('cancelled');
    const response = await fetcher(`/langua/cartus_chats/${chatId}/messages/${messageId}/${feature}`, {
      method: 'GET', credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal
    });
    if (!response.ok) throw new Error('unavailable');
    const result = await response.json();
    if (result.status === 'idle') return null;
    if (result.status === 'completed') {
      if (feature === 'correction') {
        if (typeof result.correction !== 'string' || !result.correction.trim() || new TextEncoder().encode(result.correction).length > 8000) throw new Error('invalid');
      } else if (!Array.isArray(result.suggestions) || result.suggestions.length !== 2 ||
        !result.suggestions.every(text => typeof text === 'string' && text.trim() && new TextEncoder().encode(text).length <= 2000)) throw new Error('invalid');
      return result;
    }
    if (!['queued', 'running'].includes(result.status)) throw new Error('unavailable');
    await sleep(1500);
  }
  throw new Error('timeout');
}

export function attachChatAutomation(chat, { page = document, windowTarget = window, fetcher = fetch } = {}) {
  const form = chat.chatForm;
  if (form?.dataset.serverMediatedAi !== 'true') return { completed() {}, destroy() {} };
  const active = new Map(), seen = new Set(); let destroyed = false;
  async function render(node, feature, result, display, signal) {
    if (feature === 'correction') {
      if (!node.querySelector('.chat-corrections')) await chat.showCorrection($(node), result.correction);
    } else {
      chat.suggestions[node.dataset.chatMessageId] = { suggestions: result.suggestions };
      if (display) await chat.showAutomaticSuggestions(node, result.suggestions, { isCurrent: () => !destroyed && !signal.aborted });
    }
  }
  async function load(node, feature, display = true, request = false) {
    const id = node?.dataset.chatMessageId;
    const key = `${feature}:${id}`;
    if (!UUID.test(id || '') || destroyed || active.has(key) || seen.has(key)) return;
    seen.add(key);
    const controller = new AbortController(); active.set(key, controller);
    const timer = setTimeout(() => controller.abort(), 75000);
    try {
      const result = request
        ? { suggestions: await requestSuggestions(chat.chatId, id, page.querySelector('meta[name="csrf-token"]')?.content, { fetcher, signal: controller.signal }) }
        : await readAutomaticResult(chat.chatId, id, feature, { fetcher, signal: controller.signal });
      if (result && !destroyed && !controller.signal.aborted && node.isConnected) await render(node, feature, result, display, controller.signal);
    } catch (_) {
      if (!destroyed && !controller.signal.aborted && node.isConnected) {
        const output = page.createElement('div'); output.className = 'alert alert-danger js-error-message font-weight-bold'; output.setAttribute('role', 'alert');
        output.textContent = I18n.t('javascript.chat.errors_persist_contact'); chat.chatElement.appendChild(output);
      }
    } finally { clearTimeout(timer); active.delete(key); }
  }
  function completed(id, display = true) {
    if (destroyed || !UUID.test(id || '')) return;
    const nodes = [...page.querySelectorAll('.chat-content .chat-message[data-chat-message-id]')];
    const index = nodes.findIndex(node => node.dataset.chatMessageId === id && !node.classList.contains('self'));
    if (index < 0) return;
    if (form.dataset.automaticSuggestions === 'true') load(nodes[index], 'suggestions', display);
    if (form.dataset.automaticCorrections === 'true') {
      const learner = nodes.slice(0, index).reverse().find(node => node.classList.contains('self'));
      if (learner) load(learner, 'correction');
    }
  }
  function settingsSaved(event) {
    const options = event.detail || {};
    for (const [key, field, gate, feature] of [['guidedMode', 'automaticSuggestions', 'suggestionsEnabled', 'suggestions'], ['autoCorrect', 'automaticCorrections', 'correctionsEnabled', 'correction']]) {
      if (!(key in options)) continue;
      const wasEnabled = form.dataset[field] === 'true';
      form.dataset[field] = String(form.dataset[gate] === 'true' && options[key] === true);
      if (form.dataset[field] !== 'true') {
        active.forEach((controller, id) => { if (id.startsWith(feature + ':')) controller.abort(); });
        if (feature === 'suggestions') chat.guidedModeService?.hide();
      } else if (feature === 'suggestions' && !wasEnabled) {
        const nodes = [...page.querySelectorAll('.chat-content .chat-message:not(.self):not(.loading)[data-chat-message-id]')];
        const node = nodes[nodes.length - 1];
        if (node) { seen.delete(`suggestions:${node.dataset.chatMessageId}`); load(node, 'suggestions', true, true); }
      }
    }
  }
  function submitted() {
    chat.tenantUiGeneration = (chat.tenantUiGeneration || 0) + 1;
    chat.tenantSuggestionAbort?.abort();
    active.forEach(controller => controller.abort()); chat.guidedModeService?.hide();
  }
  form.addEventListener('submit', submitted);
  page.addEventListener('cartus:chat-settings-saved', settingsSaved);
  function destroy() {
    submitted();
    form.removeEventListener('submit', submitted);
    page.removeEventListener('cartus:chat-settings-saved', settingsSaved);
    destroyed = true; active.forEach(controller => controller.abort()); active.clear();
    page.removeEventListener('turbolinks:before-cache', destroy); page.removeEventListener('turbo:before-cache', destroy); windowTarget.removeEventListener('pagehide', destroy);
  }
  page.addEventListener('turbolinks:before-cache', destroy); page.addEventListener('turbo:before-cache', destroy); windowTarget.addEventListener('pagehide', destroy);
  // Recover already reserved results from history, never backfill inference.
  const history = [...page.querySelectorAll('.chat-content .chat-message:not(.self)[data-chat-message-id]')];
  history.slice(-50).forEach(node => completed(node.dataset.chatMessageId, false));
  return { completed, submitted, destroy };
}
